import { ConnectorSourceTransformationType } from '../types';
import { isEmptyString } from '@/utils/string';
import salesforceDefaultRenames from './salesforce/default-renames';
import hubspotDefaultRenames from './hubspot/default-renames';

interface RecordDescriptor {
  name: string;
  fields: { name: string }[];
  labelPlural?: string;
}

export interface ComputedMappingDefaults {
  identifiers: Record<string, string>;
  renames: Record<string, string>;
  where_clause?: string;
  requiredFields: string[];
}

/**
 * Computes default identifiers, renames, where_clause, and required fields
 * for a given record descriptor and CDP type. Shared between the preview
 * drawer (paved road) and the full sync editor (defaultMapping).
 *
 * When emailField is provided (from sources.yaml sync templates), it is
 * used directly. Otherwise falls back to searching the record's fields.
 */
export function computeMappingDefaults(
  record: RecordDescriptor,
  cdpType: string,
  isEmailEnabled: boolean,
  emailField?: string,
  sourceOptionId?: string,
): ComputedMappingDefaults {
  const identifiers: Record<string, string> = {};
  const renames: Record<string, string> = {};
  let where_clause: string | undefined;
  const requiredFields: string[] = [];

  const recordType = record.name.toLowerCase();
  const getField = (search: string) =>
    record.fields.find((f) => f.name.toLowerCase() === search.toLowerCase())?.name ?? search;
  const hasField = (search: string) => record.fields.some((f) => f.name.toLowerCase() === search.toLowerCase());

  // Use the explicitly provided email field, or search the record's fields.
  const resolvedEmailField = !isEmptyString(emailField)
    ? emailField
    : (record.fields.find(
        (f) =>
          f.name.toLowerCase() === 'email' ||
          f.name.toLowerCase() === 'properties_email' ||
          f.name.toLowerCase() === 'properties.email',
      )?.name ?? null);

  if (cdpType === ConnectorSourceTransformationType.GROUP) {
    const idFieldName = getField('Id');
    identifiers.group_id = idFieldName;
    identifiers.object_name = record.labelPlural ?? record.name;
    requiredFields.push(idFieldName);
  } else if (cdpType === ConnectorSourceTransformationType.RELATIONSHIP) {
    identifiers.object_name = record.labelPlural ?? record.name;
    // For relationship records that have a Contact.Email field, default to
    // using it as the person ID and filter out records without an email.
    const hasContactEmail = isEmailEnabled && hasField('Contact.Email');
    if (hasContactEmail) {
      const contactEmailField = getField('Contact.Email');
      identifiers.user_id = contactEmailField;
      requiredFields.push(contactEmailField);
      where_clause = 'Contact.Email != null';

      // Use the first non-Contact *.Id field as the object ID.
      const objectIdField = record.fields.find(
        (f) => f.name.toLowerCase().endsWith('.id') && !f.name.toLowerCase().startsWith('contact.'),
      );
      if (objectIdField != null) {
        identifiers.group_id = objectIdField.name;
        requiredFields.push(objectIdField.name);
      }
    }
  } else if ((recordType === 'lead' || recordType === 'leads') && resolvedEmailField != null) {
    // When email is enabled, use Email as Lead's default identifier so that
    // when a Lead converts to a Contact the email allows auto-merging the
    // records in Journeys while keeping the Lead Id as an attribute.
    const idFieldName = getField('Id');
    requiredFields.push(idFieldName);
    identifiers.user_id = idFieldName;

    if (isEmailEnabled) {
      identifiers.user_id = resolvedEmailField;
      renames[idFieldName] = 'lead_id';
      requiredFields.push(resolvedEmailField);
      where_clause = `${resolvedEmailField} != null`;
    }
  } else if ((recordType === 'contact' || recordType === 'contacts') && isEmailEnabled && resolvedEmailField != null) {
    // When email is enabled, use Email as the Contact identifier so profiles
    // can be merged in Journeys. Keep Id in fields as an attribute.
    const idFieldName = getField('Id');
    identifiers.user_id = resolvedEmailField;
    renames[idFieldName] = 'contact_id';
    requiredFields.push(resolvedEmailField, idFieldName);
    where_clause = `${resolvedEmailField} != null`;
  } else {
    const idFieldName = getField('Id');
    identifiers.user_id = idFieldName;
    requiredFields.push(idFieldName);
  }

  if (sourceOptionId != null) {
    const staticRenames: Record<string, string> =
      sourceOptionId === 'hubspot'
        ? (hubspotDefaultRenames[cdpType] ?? {})
        : ((salesforceDefaultRenames as Record<string, Record<string, string>>)[cdpType] ?? {});

    for (const [key, value] of Object.entries(staticRenames)) {
      if (!(key in renames)) {
        renames[key] = value;
      }
    }

    for (const field of record.fields) {
      if (sourceOptionId !== 'salesforce' && field.name.startsWith('properties.')) {
        if (!(field.name in renames)) {
          renames[field.name] = field.name.substring('properties.'.length);
        }
      }

      if (sourceOptionId === 'salesforce' && field.name === 'Id' && record.name !== '') {
        if (!(field.name in renames)) {
          renames[field.name] = `${record.name}Id`;
        }
      }
    }
  }

  return { identifiers, renames, where_clause, requiredFields };
}
